import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

interface Stream {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  streamType: 'event' | 'ongoing' | 'thematic';
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  createdAt: string;
  modifiedAt: string;
  // Counts are now computed, not stored
  storyCount?: number;
  claimCount?: number;
}

interface Story {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  storyType: string;
  createdAt: string;
  modifiedAt: string;
  isActive: boolean;
  // Counts are now computed, not stored
  materialCount?: number;
  claimCount?: number;
}

interface StreamsResponse {
  streams: Stream[];
  total: number;
}

interface StreamDetailResponse {
  stream: Stream;
  stories: Story[];
}

interface StoriesResponse {
  stories: Array<{
    id: string;
    title: string;
    slug: string;
    summary: string | null;
    isActive: boolean;
    createdAt: string;
    modifiedAt: string;
    streamId: string | null;
    streamTitle: string | null;
    streamSlug: string | null;
    // Counts are now computed, not stored
    materialCount?: number;
    claimCount?: number;
  }>;
  hasMore: boolean;
}

export function useStreams(options?: { type?: string; active?: boolean }) {
  return useQuery({
    queryKey: ['streams', options],
    queryFn: async (): Promise<StreamsResponse> => {
      const params: Record<string, string | number | undefined> = {};
      if (options?.type) params.type = options.type;
      if (options?.active !== undefined) params.active = options.active ? 'true' : 'false';

      return apiClient.get<StreamsResponse>('/streams', params);
    },
  });
}

export function useStream(id: string) {
  return useQuery({
    queryKey: ['stream', id],
    queryFn: async (): Promise<StreamDetailResponse> => {
      return apiClient.get<StreamDetailResponse>(`/streams/${id}`);
    },
    enabled: !!id,
  });
}

export function useUnassignedStories() {
  return useQuery({
    queryKey: ['stories', 'unassigned'],
    queryFn: async (): Promise<StoriesResponse> => {
      return apiClient.get<StoriesResponse>('/stories', { unassigned: 'true', limit: 50 });
    },
  });
}

interface StreamSuggestion {
  storyId: string;
  storyTitle: string;
  suggestedStream: {
    id: string;
    title: string;
    slug: string;
  } | null;
  score: number;
  sharedEntities: { name: string; type: string }[];
  confident: boolean;
  reason: string;
}

interface SuggestionsResponse {
  suggestions: StreamSuggestion[];
  message?: string;
}

export function useStreamSuggestions(limit = 20) {
  return useQuery({
    queryKey: ['streams', 'suggestions', limit],
    queryFn: async (): Promise<SuggestionsResponse> => {
      return apiClient.get<SuggestionsResponse>('/streams/suggest', { limit });
    },
  });
}
